import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { Icons } from '@/components/ui/Icons'
import { Modal } from '@/components/ui/Modal'
import { LoadingBlock } from '@/components/ui/Spinner'
import { BlacklistDialog } from '@/features/proxies/BlacklistDialog'
import { ChannelDetailsDialog } from '@/features/ratescreener/ChannelDetailsDialog'
import { SentinelChannelDialog } from '@/features/sentinel/SentinelChannelDialog'
import { matchesLocator } from '@/features/settings/policyTarget'
import { SettingsTableDialog } from '@/features/settings/SettingsTableDialog'
import { AREA_ORDER, type AttentionItem, type EntryTarget } from './attention'
import styles from './AttentionDialog.module.css'
import { ItemCard } from './AttentionItem'
import { AttentionOverview } from './AttentionOverview'
import { ATTENTION_CHANNEL_HOURS } from './useAttention'

type View = { kind: 'overview' } | { kind: 'item'; id: string } | { kind: 'list' }

interface AttentionDialogProps {
  items: AttentionItem[]
  loading: boolean
  onClose: () => void
}

/**
 * Opens on a one-screen overview (one tile per item); a tile shows that item in full,
 * and "All details" lists everything grouped by product, as before.
 */
export function AttentionDialog({ items, loading, onClose }: AttentionDialogProps) {
  const [view, setView] = useState<View>({ kind: 'overview' })
  const [target, setTarget] = useState<EntryTarget | null>(null)
  // An item that got resolved by a refresh simply drops back to the overview.
  const focused = view.kind === 'item' ? items.find((i) => i.id === view.id) : undefined
  const groups = AREA_ORDER.map((area) => ({ area, items: items.filter((i) => i.area === area) })).filter((g) => g.items.length > 0)

  const toggle = (
    <div className={styles.viewToggle}>
      <Button size="sm" active={view.kind !== 'list'} icon={<Icons.Grid size={13} />} onClick={() => setView({ kind: 'overview' })}>
        Overview
      </Button>
      <Button size="sm" active={view.kind === 'list'} onClick={() => setView({ kind: 'list' })}>
        All details
      </Button>
      {loading && items.length > 0 && <span className="u-faint">Updating…</span>}
    </div>
  )

  return (
    <Modal title="Needs attention" onClose={onClose} size="lg" headerExtra={toggle}>
      {loading && items.length === 0 ? (
        <LoadingBlock />
      ) : items.length === 0 ? (
        <EmptyState>Nothing needs attention right now.</EmptyState>
      ) : view.kind === 'list' ? (
        groups.map((group) => (
          <section key={group.area} className={styles.group}>
            <h3 className={styles.groupTitle}>{group.area}</h3>
            <div className={styles.items}>
              {group.items.map((item) => (
                <ItemCard key={item.id} item={item} onOpen={setTarget} />
              ))}
            </div>
          </section>
        ))
      ) : focused ? (
        <div className={styles.focus}>
          <Button size="sm" variant="ghost" icon={<Icons.Grid size={13} />} onClick={() => setView({ kind: 'overview' })}>
            Back to overview
          </Button>
          <ItemCard item={focused} onOpen={setTarget} />
        </div>
      ) : (
        <AttentionOverview items={items} onSelect={(item) => setView({ kind: 'item', id: item.id })} />
      )}

      {target?.kind === 'channel' && <ChannelDetailsDialog channel={target.channel} hours={ATTENTION_CHANNEL_HOURS} onClose={() => setTarget(null)} />}
      {target?.kind === 'sentinel' && <SentinelChannelDialog channel={target.channel} onClose={() => setTarget(null)} />}
      {target?.kind === 'blacklist' && <BlacklistDialog scope={target.scope} onClose={() => setTarget(null)} />}
      {target?.kind === 'policy' && <SettingsTableDialog tableKey={target.tableKey} focusRow={(row) => matchesLocator(row, target)} onClose={() => setTarget(null)} />}
    </Modal>
  )
}
